import {Component, Input, OnInit} from "@angular/core";
import {FormBuilder, FormGroup} from "@angular/forms";
import {faSort, faSortDown, faSortUp} from "@fortawesome/free-solid-svg-icons";
import {TranslateService} from "@ngx-translate/core";
import {Observable, of} from "rxjs";
import {CatalogApiService} from "src/app/api/catalog-api.service";
import {CareItem, OrderItem, ArticleType, CareItemType} from "src/app/model/order/order";
import {SortOrder} from "src/app/model/pagination/sort";
import {CartService} from "src/app/utils/cart/cart.service";
import {CareAndAccessoriesService} from "../care-and-accessories.service";

@Component({
  selector: 'app-care',
  templateUrl: './care.component.html',
  styleUrls: ['./care.component.scss']
})
export class CareComponent implements OnInit {

  @Input() ordering = true;

  originalCareItems: CareItem[];
  filteredCareItems: Observable<OrderItem[]>;
  order: SortOrder = SortOrder.ASC;

  form: FormGroup;

  constructor(private careAndAccessoriesService: CareAndAccessoriesService,
              private formBuilder: FormBuilder,
              private cartService: CartService,
              private catalogApiService: CatalogApiService,
              private translateService: TranslateService) {
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      searchCareInput: [''],
      isSoft: [true],
      isHard: [true],
      isEye: [true],
      isRewetting: [true]
    });

    this.catalogApiService.getCareItems().subscribe(data => {
      this.originalCareItems = data;
      this.originalCareItems.forEach(item => {
        item.units = 1;
      });
      this.filterCareItems();
    });

    this.form.get('searchCareInput').valueChanges.subscribe(() => this.filterCareItems());
    this.form.get('isSoft').valueChanges.subscribe(() => this.filterCareItems());
    this.form.get('isHard').valueChanges.subscribe(() => this.filterCareItems());
    this.form.get('isEye').valueChanges.subscribe(() => this.filterCareItems());
    this.form.get('isRewetting').valueChanges.subscribe(() => this.filterCareItems());
  }

  sortByName(intermediateArray?: CareItem[]): CareItem[] {
    if (intermediateArray) {
      return intermediateArray.sort((firstItem, secondItem) => {
        if (firstItem.name < secondItem.name) {
          if (this.order === SortOrder.ASC) {
            return -1;
          } else {
            return 1;
          }
        }
        if (firstItem.name > secondItem.name) {
          if (this.order === SortOrder.ASC) {
            return 1;
          } else {
            return -1;
          }
        }
        return 0;
      });
    } else {
      this.order = (this.order === SortOrder.ASC) ? SortOrder.DESC : SortOrder.ASC;
      this.filterCareItems();
    }
  }

  public addCareItem(item: CareItem, quantity: number) {
    if (quantity < 1) {
      quantity = 1;
    }
    if (quantity > 999) {
      quantity = 999;
    }
    this.cartService.addCartCareAccessoriesItem(this.careAndAccessoriesService.convertCareAccessoriesToOrderItem(item, ArticleType.CARE, +quantity));
  }

  private filterCareItems() {
    let isSoft = this.form.get('isSoft').value;
    let isHard = this.form.get('isHard').value;
    let isEye = this.form.get('isEye').value;
    let isRewetting = this.form.get('isRewetting').value;
    let search = this.form.get('searchCareInput').value;

    if (isSoft && isHard && isEye && isRewetting) {
      this.filteredCareItems = of(this.filterCare(search, this.sortByName(this.originalCareItems)));
    } else {
      let intermediateArray: CareItem[] = [];
      if (this.form.get('isSoft').value) {
        Array.prototype.push.apply(intermediateArray, this.originalCareItems.filter(careItem => careItem.careTypes.includes(CareItemType.SOFT)));
      }

      if (this.form.get('isHard').value) {
        Array.prototype.push.apply(intermediateArray, this.originalCareItems.filter(careItem => careItem.careTypes.includes(CareItemType.HARD)));
      }

      if (this.form.get('isEye').value) {
        Array.prototype.push.apply(intermediateArray, this.originalCareItems.filter(careItem => careItem.careTypes.includes(CareItemType.EYE)));
      }

      if (this.form.get('isRewetting').value) {
        Array.prototype.push.apply(intermediateArray, this.originalCareItems.filter(careItem => careItem.careTypes.includes(CareItemType.REWETTING)));
      }

      this.filteredCareItems = of(this.filterCare(search, [...new Map(this.sortByName(intermediateArray).map(item => [item.id, item])).values()]));
    }
  }

  private filterCare(value: string, intermediateArray: CareItem[]): CareItem[] {
    const filterValue = this.normalizeValue(value);
    return intermediateArray.filter(careItem => this.normalizeValue(careItem.name).includes(filterValue));
  }

  private normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  getInfo(item: CareItem) {
    let packageCount: string;
    this.translateService.get("newOrder.equipment.care.packageCount").subscribe(text => packageCount = text.replace("{{count}}", item.quantity));
    return `${item.description}\n\n${packageCount}`;
  }

  clearSearchCareInput() {
    this.form.get('searchCareInput').setValue('');
  }

  getSortIcon() {
    if (!this.order) {
      return faSort;
    } else if (this.order === SortOrder.ASC) {
      return faSortDown;
    } else if (this.order === SortOrder.DESC) {
      return faSortUp;
    } else {
      throw Error("Unexpected sort order: " + this.order);
    }
  }

  getDiscountHint(priceInfo: string): string[] {
    return priceInfo.split("; ");
  }

  getExclamationHint(): string {
    let hint: string = "";
    this.translateService.get("newOrder.equipment.electronicOrdering").subscribe(data => hint = data);
    return hint;
  }

  switchOrdering(item: OrderItem, id: number) {
    let request = this.catalogApiService.switchOrdering(id);
    request.subscribe((response) => {
      if (response.success) {
        item.electronicOrdering = !item.electronicOrdering;
        console.log("Successfully switched electronic ordering for article: " + id);
      } else {
        console.log("Failed to switch electronic ordering");
        console.log(response.httpError);
      }
    });
  }
}
